import { postData } from './global';
import { apiEndpoints } from '@config/env';
import { debug, warn, error as logError } from './console';

export type EnsureLiveDatasetsOptions = {
  /** Delete all existing LIVE dataset rows (and cascaded events) before creating fresh rows. */
  reset?: boolean;
};

export type LiveImportPair = {
  source_id: number;
  date: string;
};

export type DeleteLiveForImportResult = {
  deleted: number;
  ok: boolean;
};

/** Flatten datesBySourceArray maps into unique source+date pairs for import cleanup. */
export function collectLiveImportPairs(
  datesBySourceArrays: Map<number, string[]>[]
): LiveImportPair[] {
  const seen = new Set<string>();
  const pairs: LiveImportPair[] = [];

  for (const datesBySource of datesBySourceArrays) {
    if (!datesBySource || datesBySource.size === 0) {
      continue;
    }
    for (const [sourceId, dates] of datesBySource.entries()) {
      if (!sourceId || !Array.isArray(dates)) {
        continue;
      }
      const uniqueDates = [...new Set(dates)];
      for (const date of uniqueDates) {
        if (!date) {
          continue;
        }
        const key = `${sourceId}|${date}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        pairs.push({ source_id: Number(sourceId), date: String(date) });
      }
    }
  }

  return pairs;
}

/**
 * Delete datasets with tags.isLive for source+date pairs before upload import.
 * POST /api/datasets/delete-live-for-import
 */
export async function deleteLiveDatasetsForImport(
  projectId: number,
  className: string,
  pairs: LiveImportPair[]
): Promise<DeleteLiveForImportResult> {
  if (!projectId || projectId <= 0 || !className?.trim() || pairs.length === 0) {
    return { deleted: 0, ok: true };
  }

  try {
    const response = await postData(`${apiEndpoints.app.datasets}/delete-live-for-import`, {
      class_name: className,
      project_id: projectId,
      pairs,
    });
    if (!response.success) {
      warn('[liveDatasets] delete-live-for-import failed:', response.message);
      return { deleted: 0, ok: false };
    }
    const deleted = Array.isArray(response.data?.deleted) ? response.data.deleted.length : 0;
    debug('[liveDatasets] LIVE datasets removed for import', { deleted, pairs: pairs.length });
    return { deleted, ok: true };
  } catch (err: unknown) {
    logError('[liveDatasets] delete-live-for-import error:', err as Error);
    return { deleted: 0, ok: false };
  }
}

/**
 * Housekeep today's LIVE dataset rows (stale delete, or a full reset when `options.reset`).
 * Does not insert a row per project source. A LIVE dataset is added only after that source
 * returns streaming data (server_stream → POST /api/datasets/ensure-live with source_names).
 */
export async function ensureLiveDatasetsForProject(
  projectId: number,
  className: string,
  options?: EnsureLiveDatasetsOptions
): Promise<boolean> {
  if (!projectId || projectId <= 0 || !className?.trim()) {
    return false;
  }
  try {
    const response = await postData(`${apiEndpoints.app.datasets}/ensure-live`, {
      class_name: className,
      project_id: projectId,
      event_name: 'NA',
      ...(options?.reset === true ? { reset: true } : {}),
    });
    if (!response.success) {
      warn('[liveDatasets] ensure-live failed:', response.message);
      return false;
    }
    debug('[liveDatasets] LIVE datasets ensured', response.data);
    return true;
  } catch (err: unknown) {
    logError('[liveDatasets] ensure-live error:', err as Error);
    return false;
  }
}
